import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../features/auth/store'
import { usePermissions, Permission } from '../hooks/usePermissions'
import { MerchantPicker } from './MerchantPicker'
import { isPathActive } from '../utils/isPathActive'

interface SidebarProps {
  /** Called when navigating - used to close mobile sidebar */
  onNavigate?: () => void
}

export const Sidebar = ({ onNavigate }: SidebarProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout, user } = useAuthStore()
  const { hasPermission, hasAnyPermission } = usePermissions()

  // A user manages merchant-scoped pages "per-merchant" only if they can open the merchant
  // detail page — i.e. they hold MERCHANT_VIEW. That (not roleScope) is the correct gate:
  // COLLECTIVE_USER is COLLECTIVE-scoped but has NO MERCHANT_VIEW, so hiding these from them
  // would strand the pages (they can't reach the merchant-detail fallback).
  const canManageViaMerchantDetail = hasPermission(Permission.MERCHANT_VIEW)

  // `group: undefined` renders above/outside any section (Dashboard). Groups render in this
  // array's order, with a collapsible header only when they have ≥1 permission-visible item.
  //
  // `merchantScoped: true` = a page that is inert until a single merchant is chosen (it shows
  // a "Select a merchant…" empty state). TWO different views by role:
  //   • Users WITHOUT MERCHANT_VIEW (MERCHANT admins/users, COLLECTIVE_USER) have no merchant
  //     list — these ARE their sidebar, auto-scoped to their own/selected merchant, so they
  //     stay visible.
  //   • Users WITH MERCHANT_VIEW (COLLECTIVE admins) manage these PER-MERCHANT from the
  //     merchant detail page, so they're hidden from their sidebar (which would force a picker).
  // Cross-merchant/aggregate pages (Orders, Products, Invoices, Payments, Disputes,
  // Reconciliation) are NOT merchantScoped — they stay for everyone with the permission.
  //
  // Storefront provisioning is intentionally NOT here — it's a COLLECTIVE action on the
  // merchant list + a status card in merchant detail. Product Metadata likewise lives in
  // the merchant detail page. TCS Config / Intake Catalog are platform-wide configuration.
  const allMenuItems = [
    { name: 'Dashboard', path: '/', permission: Permission.DASHBOARD_VIEW },
    // Tenants (brand-website) — TENANT_VIEW gates the page, but MERCHANT-scoped
    // users also hold TENANT_VIEW purely so the API lets them read their own
    // (ECOMMERCE) tenant; the brand-website list would always be empty for them,
    // so hide the entry from MERCHANT scope. COLLECTIVE staff see all tenants and
    // TENANT-scoped users see their own.
    ...(user?.roleScope !== 'MERCHANT'
      ? [{ name: 'Tenants', path: '/tenants', permission: Permission.TENANT_VIEW, group: 'Commerce' }]
      : []),
    { name: 'Merchants', path: '/merchants', permission: Permission.MERCHANT_VIEW, group: 'Commerce' },
    // Read-only fleet view of go-live readiness across all storefront tenants (the
    // per-merchant provisioning ACTIONS stay on merchant detail — see note above).
    { name: 'Storefronts', path: '/storefronts', permission: Permission.MERCHANT_VIEW, group: 'Commerce' },
    // Merchant applications (PE-1282) — self-onboarded merchants awaiting TCS
    // approval. Not merchantScoped: a cross-merchant queue, not a per-merchant page.
    { name: 'Merchant Applications', path: '/merchant-applications', permission: Permission.MERCHANT_ONBOARDING_VIEW, group: 'Commerce' },
    { name: 'Orders', path: '/orders', permission: Permission.ORDER_READ, group: 'Commerce' },
    { name: 'Custom Orders', path: '/custom-orders', permission: Permission.ORDER_READ, group: 'Commerce', merchantScoped: true },
    { name: 'Booking Services', path: '/booking-services', permission: Permission.BOOKING_SERVICE_VIEW, group: 'Commerce', merchantScoped: true },
    { name: 'Branches', path: '/branches', permission: Permission.BRANCH_VIEW, group: 'Commerce', merchantScoped: true },
    { name: 'Packages & Add-ons', path: '/packages', permission: Permission.BOOKING_SERVICE_VIEW, group: 'Commerce', merchantScoped: true },
    // Returns policy (PE-2163) — watchman resolves the merchant from the JWT on
    // `PATCH merchants/me/store-config`, so only a MERCHANT-scoped session can edit it;
    // TCS staff read it on the merchant detail page like the other merchantScoped entries.
    { name: 'Returns & Exchanges', path: '/returns-policy', permission: Permission.ORDER_READ, group: 'Commerce', merchantScoped: true },
    // Shipping zones (PE-2172/PE-2173) — scoped purely by x-tenant-id (see shippingZonesApi.ts),
    // so both TCS staff and a merchant-scoped session can manage it for the selected merchant.
    { name: 'Shipping zones', path: '/shipping-zones', permission: Permission.ORDER_READ, group: 'Commerce', merchantScoped: true },
    {
      name: 'Product Catalogue',
      path: '/products',
      permissions: [Permission.PRODUCT_READ_ALL, Permission.PRODUCT_READ_PUBLISHED],
      group: 'Catalogue',
    },
    // Reviews & testimonials moderation (PE-2171) — tenant-wide curation queue.
    { name: 'Reviews & testimonials', path: '/reviews', permission: Permission.PRODUCT_READ_ALL, group: 'Catalogue', merchantScoped: true },
    { name: 'Homepage Slider', path: '/homepage-slider', permission: Permission.SLIDER_VIEW, group: 'Catalogue', merchantScoped: true },
    // Portfolio (PE-1831 follow-up) — merchant showcase gallery, unified across charter/
    // atelier/rack-and-box (osprey/src/features/portfolio). Replaces the old charter-only
    // "Charter Portfolio" entry below.
    { name: 'Portfolio', path: '/portfolio', permission: Permission.PORTFOLIO_VIEW, group: 'Catalogue', merchantScoped: true },
    { name: 'Blog Posts', path: '/blog', permission: Permission.BLOG_POST_VIEW, group: 'Content', merchantScoped: true },
    { name: 'Blog Categories', path: '/blog/categories', permission: Permission.BLOG_CATEGORY_VIEW, group: 'Content', merchantScoped: true },
    { name: 'Pages & policies', path: '/content-pages', permission: Permission.CONTENT_PAGE_VIEW, group: 'Content', merchantScoped: true },
    // Merchant self-service — only MERCHANT_ADMIN/MERCHANT_USER hold MERCHANT_CONTRACT_VIEW,
    // so this stays hidden from COLLECTIVE staff (who read a merchant's terms on merchant detail).
    { name: 'My Contract', path: '/my-contract', permission: Permission.MERCHANT_CONTRACT_VIEW, group: 'Finance' },
    { name: 'Invoices', path: '/invoices', permission: Permission.INVOICE_VIEW, group: 'Finance' },
    { name: 'Payments', path: '/payments', permission: Permission.PAYMENT_VIEW, group: 'Finance' },
    { name: 'Payouts', path: '/payouts', permission: Permission.SETTLEMENT_VIEW, group: 'Finance', merchantScoped: true },
    { name: 'Disputes', path: '/disputes', permission: Permission.PAYMENT_DISPUTE_VIEW, group: 'Finance' },
    { name: 'Reconciliation', path: '/reconciliation', permission: Permission.RECONCILIATION_VIEW, group: 'Finance' },
    // Sales pricing (PE-2143) — TCS-staff quoting + sales-invoice engine. Not
    // merchantScoped: it prices prospects, who aren't merchants yet.
    { name: 'Sales Pricing', path: '/sales-pricing', permission: Permission.SALES_PRICING_VIEW, group: 'Finance' },
    // CRM-lite contacts (PE-2159) — prospects and conversations. Not merchantScoped, for the
    // same reason as Sales Pricing: a contact is a PROSPECT, who is not a merchant yet.
    { name: 'Contacts', path: '/contacts', permission: Permission.SALES_CONTACT_VIEW, group: 'Finance' },
    // Outreach kit (PE-2188) and referral program (PE-2190) — TCS-staff sales tooling over the
    // same prospects as Contacts, so not merchantScoped either.
    { name: 'Outreach', path: '/outreach', permission: Permission.SALES_OUTREACH_VIEW, group: 'Finance' },
    { name: 'Referrals', path: '/referrals', permission: Permission.REFERRAL_VIEW, group: 'Finance' },
    // Platform reporting (PE-2108) — TCS-wide aggregate across all merchants.
    { name: 'Reporting', path: '/reporting', permission: Permission.REPORTING_VIEW, group: 'Finance' },
    // Monthly Intelligence Report (PE-2141). Gated on INTELLIGENCE_REPORT_VIEW, a
    // collective-only constant that mirrors osprey-api's CollectiveOnlyGuard rather
    // than the watchman resource — merchants hold INTELLIGENCE_REPORT:READ but are
    // 403'd by the guard, so keying off the resource would show them a page that
    // fails on load (same reasoning as Email Campaigns below). Not merchantScoped:
    // the merchant is a filter on the queue, not a precondition for the page.
    { name: 'Intelligence Reports', path: '/intelligence-reports', permission: Permission.INTELLIGENCE_REPORT_VIEW, group: 'Finance' },
    // Search analytics (PE-1288) — per-merchant search terms/volume/no-result
    // reporting. Not merchantScoped: like Reporting/Intelligence Reports, the
    // merchant is a filter the page itself prompts for (sidebar MerchantPicker),
    // not a precondition managed from the merchant detail page.
    { name: 'Search Analytics', path: '/search-analytics', permission: Permission.SEARCH_ANALYTICS_VIEW, group: 'Finance' },
    { name: 'Contract Templates', path: '/contract-templates', permission: Permission.CONTRACT_TEMPLATE_VIEW, group: 'Configuration' },
    // Self-onboarding field configuration (PE-2181) — global platform rows governing the
    // public sign-up form, so NOT merchantScoped. Same gate as Merchant Applications, which
    // is the queue these settings feed.
    { name: 'Sign-up Fields', path: '/onboarding-fields', permission: Permission.MERCHANT_ONBOARDING_VIEW, group: 'Configuration' },
    // Instagram feed (PE-2178). TWO surfaces for TWO audiences, because watchman's
    // `me/instagram/*` routes resolve the merchant from the JWT — a COLLECTIVE session has no
    // merchantId, so the merchant page genuinely cannot serve TCS staff however the merchant
    // is pinned (unlike the x-tenant-id-scoped pages, which can).
    //   • merchantScoped entry → merchant admins, who authorise the connection themselves.
    //   • the two admin entries below → TCS staff, who oversee and support it.
    // The merchant-detail page carries a matching read-only card, so neither group is stranded.
    { name: 'Instagram Feed', path: '/settings/instagram', permission: Permission.SECURITY_SELF_MANAGE, group: 'Configuration', merchantScoped: true },
    { name: 'Instagram Connections', path: '/instagram/connections', permission: Permission.MERCHANT_VIEW, group: 'Configuration' },
    { name: 'Instagram App Credential', path: '/instagram/platform-credential', permission: Permission.MERCHANT_VIEW, group: 'Configuration' },
    { name: 'TCS Config', path: '/tcs-config', permission: Permission.PRODUCT_SCHEMA_MANAGE, group: 'Configuration' },
    { name: 'Schema Builder', path: '/intake-catalog', permission: Permission.PRODUCT_SCHEMA_MANAGE, group: 'Configuration' },
    // Team (PE-2109) — a merchant admin manages their own staff & roles. TEAM_VIEW
    // is granted only to MERCHANT_ADMIN, so this entry is merchant-facing only.
    { name: 'Team', path: '/team', permission: Permission.TEAM_VIEW, group: 'Configuration' },
    // Charter (PE-1831) — 5th TCS platform. Merchant back-office pages are merchantScoped
    // (visible to provider admins/users; COLLECTIVE staff manage cross-provider from the
    // "Providers"/"Oversight" entries below, which are NOT merchantScoped).
    { name: 'Charter Services', path: '/charter/services', permission: Permission.CHARTER_VIEW, group: 'Charter', merchantScoped: true },
    { name: 'Charter Packages', path: '/charter/catalogue', permission: Permission.CHARTER_VIEW, group: 'Charter', merchantScoped: true },
    { name: 'Charter Bookings', path: '/charter/bookings', permission: Permission.CHARTER_VIEW, group: 'Charter', merchantScoped: true },
    { name: 'Charter Orders', path: '/charter/orders', permission: Permission.CHARTER_VIEW, group: 'Charter', merchantScoped: true },
    { name: 'Charter Waitlist', path: '/charter/waitlist', permission: Permission.CHARTER_VIEW, group: 'Charter', merchantScoped: true },
    { name: 'Charter Settings', path: '/charter/settings', permission: Permission.CHARTER_VIEW, group: 'Charter', merchantScoped: true },
    { name: 'Charter Providers', path: '/charter/providers', permission: Permission.CHARTER_ADMIN, group: 'Charter' },
    { name: 'Charter Oversight', path: '/charter/oversight', permission: Permission.CHARTER_ADMIN, group: 'Charter' },
    { name: 'TCS Team', path: '/staff', permission: Permission.STAFF_VIEW, group: 'Admin' },
    // Departments, roles & permissions (PE-2147). Not merchantScoped — TCS-internal org structure.
    { name: 'Departments', path: '/departments', permission: Permission.DEPARTMENT_VIEW, group: 'Admin' },
    { name: 'Leads', path: '/leads', permission: Permission.LEADS_VIEW, group: 'Admin' },
    // News & offer email campaigns (PE-2125). Gated on CAMPAIGN_ADMIN_VIEW, not
    // CAMPAIGN_VIEW: merchants hold the latter but osprey-api's CollectiveOnlyGuard
    // rejects them, so keying off the resource permission would show them a page
    // that 403s. Not merchantScoped — the target merchant is chosen per campaign.
    { name: 'Email Campaigns', path: '/email-campaigns', permission: Permission.CAMPAIGN_ADMIN_VIEW, group: 'Admin' },
    // NOT merchantScoped: audit-service scopes results server-side (merchant admins see only
    // their own merchant), so the page needs no merchant picker and stays in every sidebar.
    { name: 'Feature Flags', path: '/feature-flags', permission: Permission.FEATURE_FLAG_VIEW, group: 'Configuration' },
    { name: 'Audit Logs', path: '/audit-logs', permission: Permission.AUDIT_LOG_VIEW, group: 'Admin' },
    // Account security / 2FA (PE-2157). Gated on SECURITY_SELF_MANAGE, which every
    // role holds — these are the caller's own settings and watchman asks for a
    // session, not a permission. The constant exists purely because this filter
    // needs one; see the note in features/auth/permissions.ts.
    { name: 'Security', path: '/security', permission: Permission.SECURITY_SELF_MANAGE, group: 'Admin' },
  ]
  const LOG_OUT_ITEM = { name: 'Log Out', path: '/logout', alwaysVisible: true }
  const GROUP_ORDER = ['Commerce', 'Catalogue', 'Content', 'Finance', 'Charter', 'Configuration', 'Admin']

  const visible = allMenuItems.filter((item) => {
    // Users who can open merchant detail manage merchant-scoped pages there, so those are
    // hidden from their global sidebar. Users without MERCHANT_VIEW keep them (their only path).
    if (canManageViaMerchantDetail && 'merchantScoped' in item && item.merchantScoped) return false
    if ('permissions' in item && item.permissions) return hasAnyPermission(item.permissions)
    if ('permission' in item && item.permission) return hasPermission(item.permission)
    return false
  })
  const ungroupedItems = visible.filter((item) => !('group' in item) || !item.group)
  const groupedSections = GROUP_ORDER
    .map((group) => ({ group, items: visible.filter((item) => 'group' in item && item.group === group) }))
    .filter((section) => section.items.length > 0)

  // Collapsible groups: the group holding the current route starts open; the user can
  // toggle any group and their choice sticks for the session (overrides the default).
  const activeGroup = allMenuItems.find(
    (item) => 'group' in item && item.group && isPathActive(location.pathname, item.path)
  )?.group
  const [openOverrides, setOpenOverrides] = useState<Record<string, boolean>>({})
  const isGroupOpen = (group: string) => openOverrides[group] ?? group === activeGroup
  const toggleGroup = (group: string) =>
    setOpenOverrides((prev) => ({ ...prev, [group]: !isGroupOpen(group) }))

  const handleItemClick = (item: { name: string; path: string }) => {
    if (item.name === 'Log Out') {
      logout()
      navigate('/login')
    } else {
      navigate(item.path)
    }
    // Close mobile sidebar after navigation
    onNavigate?.()
  }

  const renderItem = (item: { name: string; path: string }) => {
    const isActive = isPathActive(location.pathname, item.path)
    return (
      <button
        key={item.name}
        onClick={() => handleItemClick(item)}
        className={`w-full flex items-center gap-3 cursor-pointer px-3 py-2 text-left transition-colors text-base rounded-md ${
          isActive
            ? 'bg-muted text-foreground font-bold'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground font-normal'
          }`}
      >
        {item.name}
      </button>
    )
  }

  return (
    <aside className="w-72 bg-card text-card-foreground border-r border-border h-full flex flex-col justify-between">
      {/* min-h-0 + overflow-y-auto so a long/COLLECTIVE-scoped menu can still scroll; Log Out
          is pinned below (outside this scroll area, alongside "Powered by") so it's always
          reachable regardless of list length or scroll position. */}
      <div className="flex-1 min-h-0 overflow-y-auto themed-scrollbar">
        {user?.roleScope === 'COLLECTIVE' && (
          <div className="w-full max-w-[15rem] mx-auto p-4 pt-8 pb-0 lg:hidden">
            {/* 12px (was 0.625rem/10px): this label only renders below lg, where the audit's
              readable-text floor applies. */}
          <p className="text-[12px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Merchant Filter</p>
            <MerchantPicker />
          </div>
        )}
        <nav className="w-full max-w-[15rem] mx-auto p-4 pt-8 lg:pt-20 space-y-1">
          {ungroupedItems.map(renderItem)}
        </nav>
        {groupedSections.map(({ group, items }) => {
          const open = isGroupOpen(group)
          const containsActive = items.some((item) => isPathActive(location.pathname, item.path))
          return (
            <nav key={group} className="w-full max-w-[15rem] mx-auto px-4 pt-4 space-y-1">
              <button
                onClick={() => toggleGroup(group)}
                aria-expanded={open}
                // min-h-10: these collapse/expand headers are real controls on touch, and
                // py-1.5 left them 28px tall — under the audit's 40px floor below 1024.
                // Desktop spacing is unaffected (the row was already ~40px with its margins).
                className={`w-full flex items-center justify-between px-3 py-1.5 min-h-10 rounded-md text-xs font-bold uppercase tracking-wider transition-colors hover:text-foreground ${
                  containsActive && !open ? 'text-foreground' : 'text-muted-foreground'
                }`}
              >
                {group}
                <svg
                  width="12" height="12" viewBox="0 0 24 24" fill="none"
                  className={`transition-transform ${open ? 'rotate-90' : ''}`}
                >
                  <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {open && items.map(renderItem)}
            </nav>
          )
        })}
        <div className="h-4" />
      </div>
      <div className="w-full max-w-[15rem] mx-auto px-4 pt-3 border-t border-border">
        {renderItem(LOG_OUT_ITEM)}
      </div>
      <div className="w-full max-w-[15rem] mx-auto p-4 pt-2 mb-6 text-xs text-muted-foreground">
        Powered by <a href="https://thecollectivesynergy.com" target="_blank" rel="noopener noreferrer" className="text-muted-foreground font-semibold hover:underline">The Collective Synergy</a>
      </div>
    </aside>
  )
}
